import React from 'react';
import { ArrowRightLeft, MessageSquare, CornerDownRight } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { isV4 } from '../../lib/uiFlag';

// v9 escalationTrigger → short label. Unknown values render as-is so a new
// trigger added Lambda-side is never silently hidden.
const TRIGGER_LABEL = {
  'keyword':          'Asked for a person',
  'low-confidence':   'No KB answer',
  'specialist-offer': 'AI offered a specialist',
  'no-input':         'Caller not heard',
  'turn-cap':         'Turn limit',
  'system-error':     'AI error',
  'guardrail':        'Safety check',   // row 57: bot guardrail blocked the request
};

// Escalation context handed over from the AI leg of the call. All fields come
// straight from contact.getAttributes() (read in ContactContext on connect) —
// brandName, ai_escalation_reason, ai_last_question, ai_last_answer, and since
// v9 (2026-09-10) escalationTrigger + handoverQuestion. Anything
// absent on a given contact is simply omitted; the panel hides entirely when
// there is nothing to show, so a direct-to-agent call renders no empty card.
export default function EscalationContext() {
  const { display } = useContact();
  // Console v4 (row 234): CallerCardV4 shows these fields instead.
  if (!display || isV4()) return null;

  const esc = display.escalation ?? {};
  const brand = esc.brandName ?? display.brandName ?? null;
  // 2026-09-17: brand alone must NOT satisfy this — brand is set on nearly
  // every properly-bound call, escalated or not (confirmed live: two
  // manually-dialled outbound test calls both showed "Escalated from AI"
  // with zero actual escalation fields, purely because they had a brand
  // name attached). Only a real escalation-specific field means this
  // contact actually came from the AI leg.
  const hasAnything = esc.reason || esc.lastQuestion || esc.lastAnswer || esc.handoverQuestion;
  // Show "what the caller wanted" only when it adds information: on a keyword
  // escalation lastQuestion is the "speak to a person" line, handoverQuestion
  // is the request before it. On other triggers the two are the same line.
  const wanted = esc.handoverQuestion && esc.handoverQuestion !== esc.lastQuestion
    ? esc.handoverQuestion : null;
  if (!hasAnything) return null;

  return (
    <div className="esc-card">
      <div className="esc-head">
        <span className="esc-head-icon"><ArrowRightLeft size={13} /></span>
        <span className="esc-head-title">Escalated from AI</span>
        {brand && <span className="esc-brand">{brand}</span>}
      </div>

      {esc.reason && (
        <div className="esc-reason">
          <div className="esc-reason-label">
            Reason for escalation
            {esc.trigger && (
              <span className="esc-trigger">{TRIGGER_LABEL[esc.trigger] ?? esc.trigger}</span>
            )}
          </div>
          <div className="esc-reason-text">{esc.reason}</div>
        </div>
      )}

      {(wanted || esc.lastQuestion || esc.lastAnswer) && (
        <div className="esc-qa">
          {wanted && (
            <div className="esc-qa-row">
              <span className="esc-qa-icon"><MessageSquare size={12} /></span>
              <div>
                <div className="esc-qa-key">What the caller wanted</div>
                <div className="esc-qa-val">{wanted}</div>
              </div>
            </div>
          )}
          {esc.lastQuestion && (
            <div className="esc-qa-row">
              <span className="esc-qa-icon"><MessageSquare size={12} /></span>
              <div>
                <div className="esc-qa-key">Last customer question</div>
                <div className="esc-qa-val">{esc.lastQuestion}</div>
              </div>
            </div>
          )}
          {esc.lastAnswer && (
            <div className="esc-qa-row">
              <span className="esc-qa-icon"><CornerDownRight size={12} /></span>
              <div>
                <div className="esc-qa-key">Last AI answer</div>
                <div className="esc-qa-val">{esc.lastAnswer}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
